

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import { eventsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";
import ClientPaymentModal from "./components/ClientPaymentModal";
import VendorPaymentModal from "./components/VendorPaymentModal";
import PrintableInvoice from "./components/PrintableInvoice";
import AddEventModal from "./components/AddEventModal";
import EditEventModal from "./components/EditEventModal";
import InlineStatusBadge from "../components/InlineStatusBadge";

const statuses = ["all", "draft", "quoted", "confirmed", "completed", "cancelled"] as const;
type Status = typeof statuses[number];

interface Event {
  id: number;
  name: string;
  client: { id: number; name: string } | null;
  package: { id: number; name: string } | null;
  venue?: { id: number; name: string; location: string } | null;
  start_date: string;
  end_date: string | null;
  guest_count: number;
  status: string;
  payment_status?: string;
  event_type?: string;
  notes?: string;
  total_amount?: number;
  services_data?: any[];
  vendors_data?: any[];
  financials?: {
    income_subtotal: number;
    total_cost: number;
    profit: number;
    profit_margin: number;
    customer: { total: number; paid: number; balance: number };
    vendor_totals?: { paid: number; balance: number; cost: number };
    vendors?: any[];
  };
}

export default function EventsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { settings } = useSettings();
  const logoUrl = settings?.logoUrl || null;
  const businessName = settings?.business?.businessName || 'Shalimar';
  const isStaff = user?.role === 'staff';
  const canDelete = user?.role === 'admin' || user?.role === 'master' || user?.role === 'super_admin';
  const canViewProfit = user?.role === 'admin' || user?.role === 'master' || user?.role === 'super_admin';
  
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<Status>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Payment modals state
  const [clientPaymentEvent, setClientPaymentEvent] = useState<Event | null>(null);
  const [clientPaymentData, setClientPaymentData] = useState<{ customer: any; payments: any[] } | null>(null);
  const [vendorPaymentEvent, setVendorPaymentEvent] = useState<Event | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [printEvent, setPrintEvent] = useState<Event | null>(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  // Fetch events from API
  useEffect(() => {
    loadEvents();
  }, []);

  // Handle print events
  useEffect(() => {
    const handleAfterPrint = () => {
      if (printEvent) {
        showToast("Invoice ready - use browser's save as PDF option", "success");
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printEvent, showToast]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsApi.list();
      // Handle paginated response
      const eventsData = response.data?.data || response.data || [];
      setEvents(eventsData);
    } catch (error) {
      showToast("Failed to load events", "error");
      console.error("Error loading events:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return events.filter(e => {
      const matchStatus = activeFilter === "all" || e.status === activeFilter;
      const q = searchQuery.toLowerCase();
      const clientName = e.client?.name || e.name || "";
      const matchSearch = !q || clientName.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [activeFilter, searchQuery, events]);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this event?")) return;
    try {
      await eventsApi.delete(id);
      showToast("Event deleted successfully", "success");
      loadEvents();
    } catch (error) {
      showToast("Failed to delete event", "error");
    }
  };

  const handleStatusChange = async (eventId: number, newStatus: string) => {
    try {
      await eventsApi.update(eventId, { status: newStatus });
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, status: newStatus } : e));
      showToast("Status updated", "success");
    } catch {
      showToast("Failed to update status", "error");
    }
  };

  const openClientPaymentModal = async (event: Event) => {
    setModalLoading(true);
    try {
      const response = await eventsApi.get(event.id);
      const detail = response.data?.data || response.data;
      setClientPaymentData({
        customer: detail.financials?.customer || null,
        payments: detail.client_payments || detail.clientPayments || [],
      });
      setClientPaymentEvent(event);
    } catch {
      showToast("Failed to load event payment data", "error");
    } finally {
      setModalLoading(false);
    }
  };

  const openVendorPaymentModal = (event: Event) => {
    setVendorPaymentEvent(event);
  };

  const handleClientPaymentAdded = () => {
    loadEvents();
    if (clientPaymentEvent) openClientPaymentModal(clientPaymentEvent);
  };

  const handleVendorPaymentAdded = () => {
    loadEvents();
  };

  const printEventInvoice = (event: Event) => {
    setPrintEvent(event);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintEvent(null), 100);
    }, 100);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {statuses.map(s => {
            const count = s === "all" ? events.length : events.filter(e => e.status === s).length;
            return (
              <button
                key={s}
                onClick={() => setActiveFilter(s)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  activeFilter === s
                    ? "text-white shadow-sm gold-gradient"
                    : "bg-white text-stone-500 border border-stone-200 hover:border-stone-300"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)} ({count})
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddEventModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Past Event
          </button>
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl px-3 py-2">
            <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              placeholder="Search events..."
              className="bg-transparent text-sm outline-none w-36 placeholder-stone-400 text-stone-700"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600">Event</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600">Date</th>
                {canViewProfit && <th className="px-4 py-3 text-right text-xs font-semibold text-stone-600">Revenue</th>}
                {canViewProfit && <th className="px-4 py-3 text-right text-xs font-semibold text-stone-600">Cost</th>}
                {canViewProfit && <th className="px-4 py-3 text-right text-xs font-semibold text-stone-600">Profit</th>}
                <th className="px-4 py-3 text-center text-xs font-semibold text-stone-600">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const revenue = e.financials?.income_subtotal || e.total_amount || 0;
                const cost = e.financials?.total_cost || 0;
                const profit = e.financials?.profit || 0;
                const clientName = e.client?.name || e.name || "Unknown";
                return (
                  <tr key={e.id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-stone-800">{e.name}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{clientName}</td>
                    <td className="px-4 py-3 text-sm text-stone-600">{formatDate(e.start_date)}</td>
                    {canViewProfit && <td className="px-4 py-3 text-sm font-semibold text-stone-800 text-right">{formatCurrency(revenue)}</td>}
                    {canViewProfit && <td className="px-4 py-3 text-sm font-semibold text-stone-800 text-right">{formatCurrency(cost)}</td>}
                    {canViewProfit && <td className="px-4 py-3 text-sm font-semibold text-emerald-600 text-right">{formatCurrency(profit)}</td>}
                    <td className="px-4 py-3 text-center">
                      <InlineStatusBadge
                        status={e.status}
                        options={["draft", "quoted", "confirmed", "in_progress", "completed", "cancelled"]}
                        onChange={(newStatus) => handleStatusChange(e.id, newStatus)}
                        disabled={isStaff}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {/* Print Invoice */}
                        <button
                          onClick={() => printEventInvoice(e)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Print Invoice"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                        {/* View Details */}
                        <button
                          onClick={() => navigate(`/events/detail?id=${e.id}`)}
                          className="p-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {/* Edit Event */}
                        <button
                          onClick={() => setEditingEvent(e)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Event"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {/* Take Client Payment */}
                        {(() => {
                          const balance = e.financials?.customer?.balance || 0;
                          const isConfirmed = ['confirmed', 'in_progress', 'completed'].includes(e.status);
                          if (!isConfirmed || balance <= 0) return null;
                          return (
                            <button
                              onClick={() => openClientPaymentModal(e)}
                              className="p-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors"
                              title={`Take Payment (Balance: ${formatCurrency(balance)})`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </button>
                          );
                        })()}
                        {/* Give Vendor Payment */}
                        {(() => {
                          const vendorBalance = e.financials?.vendor_totals?.balance || 0;
                          const hasVendors = (e.financials?.vendors?.length || 0) > 0;
                          if (!hasVendors || vendorBalance <= 0) return null;
                          return (
                            <button
                              onClick={() => openVendorPaymentModal(e)}
                              className="p-2 text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                              title={`Give Vendor Payment (Due: ${formatCurrency(vendorBalance)})`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </button>
                          );
                        })()}
                        {/* Only Admin/Master can delete events */}
                        {!isStaff && canDelete && (
                          <button
                            onClick={() => handleDelete(e.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-stone-400">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            No events found
          </div>
        )}
      </div>

      {/* Cards Grid - Hidden but kept for reference */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 hidden">
        {filtered.map(e => {
          const revenue = e.financials?.income_subtotal || e.total_amount || 0;
          const cost = e.financials?.total_cost || 0;
          const profit = e.financials?.profit || 0;
          const balance = e.financials?.customer?.balance || 0;
          const margin = revenue ? Math.round((profit / revenue) * 100) : 0;
          const clientName = e.client?.name || e.name || "Unknown";
          const packageName = e.package?.name || "Custom";
          return (
            <div key={e.id} className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="px-5 pt-4 pb-3 border-b border-stone-50">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-bold text-stone-800 text-sm leading-tight">{clientName}</h3>
                  <InlineStatusBadge
                    status={e.status}
                    options={["draft", "quoted", "confirmed", "in_progress", "completed", "cancelled"]}
                    onChange={(newStatus) => handleStatusChange(e.id, newStatus)}
                    disabled={isStaff}
                  />
                </div>
                <div className="flex gap-3 text-xs text-stone-400">
                  <span>📅 {e.start_date}</span>
                  <span>👥 {e.guest_count || 0}</span>
                  <span>🎉 {packageName}</span>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-sm font-bold text-stone-800">{formatCurrency(revenue)}</div>
                    <div className="text-xs text-stone-400">Revenue</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-stone-800">{formatCurrency(cost)}</div>
                    <div className="text-xs text-stone-400">Cost</div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-emerald-600">{formatCurrency(profit)}</div>
                    <div className="text-xs text-stone-400">Profit</div>
                  </div>
                </div>
                {/* Profit bar */}
                <div>
                  <div className="flex justify-between text-xs text-stone-400 mb-1">
                    <span>Margin</span><span>{margin}%</span>
                  </div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${margin}%`, background: "linear-gradient(to right,#C9A84C,#16A34A)" }}></div>
                  </div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-stone-400">Package: <span className="text-stone-600 font-medium">{packageName}</span></span>
                  <span className="text-stone-400">Balance: <span className={balance > 0 ? 'text-amber-600' : 'text-emerald-600 font-medium'}>{formatCurrency(balance)}</span></span>
                </div>
                <div className="flex gap-2 pt-1">
                  <button 
                    onClick={() => printEventInvoice(e)}
                    className="flex-1 text-xs font-semibold py-2 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                  >
                    Print Invoice
                  </button>
                  <button 
                    onClick={() => navigate(`/events/detail?id=${e.id}`)}
                    className="flex-1 text-xs font-semibold py-2 rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                  >
                    View Details
                  </button>
                  {/* Only Admin/Master can delete events */}
                  {!isStaff && canDelete && (
                    <button 
                      onClick={() => handleDelete(e.id)}
                      className="px-3 text-xs font-semibold py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="col-span-3 text-center py-16 text-stone-400">No events found.</div>}
      </div>

      {/* Modal loading spinner */}
      {modalLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-amber-500 border-t-transparent"></div>
            <span className="text-stone-700 font-medium">Loading payment data...</span>
          </div>
        </div>
      )}

      {/* Client Payment Modal */}
      {clientPaymentEvent && clientPaymentData && (
        <ClientPaymentModal
          isOpen={!!clientPaymentEvent}
          onClose={() => { setClientPaymentEvent(null); setClientPaymentData(null); }}
          eventId={clientPaymentEvent.id}
          customer={clientPaymentData.customer}
          payments={clientPaymentData.payments}
          onPaymentAdded={handleClientPaymentAdded}
        />
      )}

      {/* Vendor Payment Modal */}
      {vendorPaymentEvent && (
        <VendorPaymentModal
          isOpen={!!vendorPaymentEvent}
          onClose={() => setVendorPaymentEvent(null)}
          eventId={vendorPaymentEvent.id}
          onPaymentAdded={handleVendorPaymentAdded}
        />
      )}

      {/* Printable Invoice */}
      {printEvent && (
        <PrintableInvoice
          event={printEvent}
          businessInfo={{
            name: businessName,
            logoUrl: logoUrl || undefined,
            tagline: settings?.business?.tagline,
            address: settings?.business?.address || "123 Marine Drive, Mumbai – 400001",
            phone: settings?.business?.phone || "+91-98765-43210",
            email: settings?.business?.email || "info@shalimar.com",
            gstNumber: settings?.business?.gstNumber,
          }}
        />
      )}

      {/* Add Event Modal */}
      <AddEventModal
        isOpen={showAddEventModal}
        onClose={() => setShowAddEventModal(false)}
        onEventAdded={loadEvents}
      />

      {/* Edit Event Modal */}
      {editingEvent && (
        <EditEventModal
          isOpen={!!editingEvent}
          onClose={() => setEditingEvent(null)}
          onEventUpdated={loadEvents}
          event={editingEvent}
        />
      )}

    </DashboardLayout>
  );
}
